import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  CheckCircleIcon,
  ShoppingCartIcon,
  QrCodeIcon,
  HeartIcon,
  MapPinIcon,
  ChevronRightIcon,
  DevicePhoneMobileIcon,
  ArrowLeftIcon,
  BoltIcon,
  CubeIcon,
  UserGroupIcon,
  CurrencyRupeeIcon,
  TruckIcon,
  ClockIcon,
  SparklesIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { useCart } from '../../context/CartContext';
import toast from 'react-hot-toast';

const OrderConfirmation = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { cart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderNumber]);

  const fetchOrderDetails = async () => {
    try {
      const response = await axios.get(`http://localhost:5000/api/orders/${orderNumber}`);
      setOrder(response.data.order);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const cartCount = cart.reduce((total, item) => total + (item.quantity || 1), 0);

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Nunito', sans-serif; line-height: 1.5; color: #1f2937; background-color: #f9fafb; }

    .fh-container { width: 100%; max-width: 1280px; margin: 0 auto; padding: 0 1rem; }
    @media (min-width: 640px) { .fh-container { padding: 0 1.5rem; } }
    @media (min-width: 1024px) { .fh-container { padding: 0 2rem; } }

    /* HEADER */
    .fh-header {
      position: sticky; top: 0; z-index: 50; width: 100%;
      transition: all 0.4s cubic-bezier(0.165,0.84,0.44,1);
    }
    .fh-header.default {
      background: rgba(255,255,255,0.7); backdrop-filter: blur(10px);
      box-shadow: 0 4px 20px rgba(0,0,0,0.02); border-bottom: 1px solid rgba(229,231,235,0.3);
    }
    .fh-header.scrolled {
      background: rgba(255,255,255,0.85); backdrop-filter: blur(16px);
      box-shadow: 0 8px 32px rgba(0,0,0,0.05); border-bottom: 1px solid rgba(5,150,105,0.15);
    }
    .fh-header-content {
      display: flex; justify-content: space-between; align-items: center;
      height: 70px; transition: height 0.3s ease;
    }
    @media (min-width: 768px) {
      .fh-header-content { height: 85px; }
      .fh-header.scrolled .fh-header-content { height: 75px; }
    }

    /* Logo — image only */
    .fh-logo { display: flex; align-items: center; cursor: pointer; }
    .fh-logo img {
      height: 36px; width: auto; object-fit: contain; transition: transform 0.3s ease;
    }
    .fh-logo:hover img { transform: scale(1.05); }
    @media (min-width: 480px) { .fh-logo img { height: 40px; } }
    @media (min-width: 768px) { .fh-logo img { height: 46px; } }

    /* Desktop Nav */
    .fh-nav { display: none; }
    @media (min-width: 768px) { .fh-nav { display: flex; align-items: center; gap: 2.5rem; } }
    .fh-nav-link {
      color: #374151; font-weight: 500; text-decoration: none;
      position: relative; transition: color 0.2s; font-size: 0.95rem;
    }
    .fh-nav-link:hover { color: #059669; }
    .fh-nav-link::after {
      content: ''; position: absolute; bottom: -4px; left: 0;
      width: 0; height: 2px; background: #059669; transition: width 0.3s;
    }
    .fh-nav-link:hover::after { width: 100%; }

    .fh-btn {
      display: inline-flex; align-items: center; justify-content: center;
      padding: 0.6rem 1.5rem; border-radius: 9999px; font-weight: 600; font-size: 0.9rem;
      transition: all 0.3s; cursor: pointer; border: none; gap: 0.5rem;
    }
    @media (min-width: 768px) { .fh-btn { padding: 0.75rem 1.75rem; font-size: 1rem; } }
    .fh-btn-primary {
      background: linear-gradient(135deg,#10b981,#06b6d4); color: white;
      box-shadow: 0 4px 6px rgba(16,185,129,0.2);
    }
    .fh-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 15px rgba(16,185,129,0.3); }

    .fh-shop-btn {
      display: flex; align-items: center; gap: 0.5rem; color: #374151;
      font-weight: 500; background: none; border: none; cursor: pointer;
      padding: 0.5rem 1.25rem; border-radius: 9999px; font-size: 0.95rem;
    }
    .fh-shop-btn:hover { color: #059669; background: rgba(243,244,246,0.7); }

    .fh-cart-badge {
      position: absolute; top: -8px; right: -8px;
      background: linear-gradient(135deg,#ef4444,#dc2626); color: white;
      font-size: 0.75rem; font-weight: 600; height: 1.25rem; width: 1.25rem;
      display: flex; align-items: center; justify-content: center;
      border-radius: 9999px; border: 2px solid white;
    }
    .fh-cart-icon {
      position: relative; display: flex; align-items: center; justify-content: center;
      padding: 0.5rem; border-radius: 9999px; transition: all 0.2s; cursor: pointer;
    }
    .fh-cart-icon:hover { background: rgba(243,244,246,0.7); }

    /* ORDER CONFIRMATION */
    .fh-confirmation-container { max-width: 48rem; margin: 2rem auto; padding: 0 1rem; }
    .fh-confirmation-card {
      background: white; border-radius: 2rem; padding: 2.5rem;
      box-shadow: 0 20px 35px -8px rgba(0,0,0,0.05);
      border: 1px solid rgba(229,231,235,0.6); position: relative; overflow: hidden;
    }
    .fh-confirmation-card::before {
      content: ''; position: absolute; top: 0; left: 0; right: 0; height: 6px;
      background: linear-gradient(90deg,#10b981,#06b6d4,#8b5cf6);
    }
    .fh-success-icon {
      width: 5rem; height: 5rem;
      background: linear-gradient(135deg,#d1fae5,#a7f3d0);
      border-radius: 9999px; display: flex; align-items: center; justify-content: center;
      margin: 0 auto 1.5rem; animation: scaleIn 0.5s ease-out;
    }
    .fh-success-icon svg { width: 3rem; height: 3rem; color: #059669; }
    @keyframes scaleIn { 0%{transform:scale(0)} 80%{transform:scale(1.1)} 100%{transform:scale(1)} }
    .fh-confirmation-title {
      font-size: 2rem; font-weight: 800;
      background: linear-gradient(135deg,#059669,#0891b2);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      margin-bottom: 0.75rem; text-align: center;
    }
    .fh-confirmation-subtitle { color: #6b7280; font-size: 1.1rem; text-align: center; margin-bottom: 2rem; }

    .fh-order-details {
      background: linear-gradient(145deg,#f9fafb,#ffffff);
      border-radius: 1.5rem; padding: 1.75rem; margin-bottom: 2rem; border: 1px solid #e5e7eb;
    }
    .fh-order-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: 0.75rem 0; border-bottom: 1px solid #e5e7eb;
    }
    .fh-order-row:last-child { border-bottom: none; }
    .fh-order-label { color: #6b7280; font-size: 0.95rem; display: flex; align-items: center; gap: 0.5rem; }
    .fh-order-label svg { width: 1.25rem; height: 1.25rem; color: #059669; }
    .fh-order-value { font-weight: 700; color: #1f2937; }
    .fh-order-number { color: #059669; font-size: 1.1rem; letter-spacing: 1px; }
    .fh-order-total { font-size: 1.5rem; color: #059669; }

    .fh-action-buttons { display: flex; flex-direction: column; gap: 1rem; margin-bottom: 1.5rem; }
    @media (min-width: 640px) { .fh-action-buttons { flex-direction: row; justify-content: center; } }
    .fh-action-btn {
      flex: 1; display: inline-flex; align-items: center; justify-content: center;
      gap: 0.75rem; padding: 1rem 1.5rem; border-radius: 9999px;
      font-weight: 700; font-size: 1rem; transition: all 0.3s; cursor: pointer; border: none;
    }
    .fh-action-btn-primary {
      background: linear-gradient(135deg,#059669,#10b981); color: white;
      box-shadow: 0 4px 6px rgba(5,150,105,0.2);
    }
    .fh-action-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 15px rgba(5,150,105,0.3); }
    .fh-action-btn-secondary { background: #f3f4f6; color: #374151; border: 2px solid #e5e7eb; }
    .fh-action-btn-secondary:hover { background: #e5e7eb; transform: translateY(-2px); }

    .fh-tracking-badge {
      display: flex; align-items: center; gap: 0.75rem;
      background: #ecfdf5; padding: 1.25rem; border-radius: 1.5rem; margin-top: 1.5rem;
    }
    .fh-tracking-badge svg { width: 1.5rem; height: 1.5rem; color: #059669; }
    .fh-tracking-title { font-weight: 700; color: #065f46; margin-bottom: 0.25rem; }
    .fh-tracking-desc { color: #047857; font-size: 0.9rem; }

    .fh-spinner {
      border: 3px solid #f3f4f6; border-top: 3px solid #059669;
      border-radius: 50%; width: 3rem; height: 3rem; animation: spin 1s linear infinite;
    }
    @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }

    /* TRUST */
    .fh-trust-banner {
      background: #f9fafb; padding: 1.5rem 0;
      border-top: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb; margin-top: 3rem;
    }

    /* FOOTER */
    .fh-footer { background: #0f172a; color: white; padding: 4rem 0 2rem; margin-top: 4rem; }
    .fh-footer-grid { display: grid; grid-template-columns: 1fr; gap: 2.5rem; margin-bottom: 3rem; }
    @media (min-width: 640px) { .fh-footer-grid { grid-template-columns: repeat(2,1fr); } }
    @media (min-width: 1024px) { .fh-footer-grid { grid-template-columns: 2fr 1fr 1fr 1.5fr; gap: 3rem; } }

    /* Footer logo — image only */
    .fh-footer-logo { display: flex; align-items: center; margin-bottom: 1.5rem; cursor: pointer; }
    .fh-footer-logo img { height: 44px; width: auto; object-fit: contain; }

    .fh-footer-about { color: #94a3b8; line-height: 1.7; margin-bottom: 1.5rem; font-size: 0.95rem; }
    .fh-footer-social { display: flex; gap: 1rem; }
    .fh-social-icon {
      width: 2.5rem; height: 2.5rem; background: #1e293b; border-radius: 0.75rem;
      display: flex; align-items: center; justify-content: center; color: white;
      transition: all 0.3s; text-decoration: none;
    }
    .fh-social-icon:hover { background: #2563eb; transform: translateY(-3px); }

    .fh-footer-title {
      font-size: 1.125rem; font-weight: 600; margin-bottom: 1.5rem;
      display: flex; align-items: center; gap: 0.5rem; color: white;
    }
    .fh-footer-heart { color: #ef4444; animation: heartbeat 1.5s ease-in-out infinite; }
    .fh-footer-links { list-style: none; padding: 0; }
    .fh-footer-links li { margin-bottom: 0.875rem; }
    .fh-footer-links a {
      color: #94a3b8; text-decoration: none; font-size: 0.95rem; transition: all 0.2s;
      display: inline-flex; align-items: center; gap: 0.5rem;
    }
    .fh-footer-links a:hover { color: white; transform: translateX(5px); }

    .fh-footer-contact { list-style: none; padding: 0; }
    .fh-footer-contact li { display: flex; align-items: flex-start; gap: 0.75rem; margin-bottom: 1rem; color: #94a3b8; font-size: 0.95rem; }
    .fh-footer-contact svg { width: 1.25rem; height: 1.25rem; color: #94a3b8; flex-shrink: 0; }

    .fh-footer-newsletter { margin-top: 1.5rem; }
    .fh-newsletter-input { display: flex; gap: 0.5rem; }
    .fh-newsletter-input input {
      flex: 1; padding: 0.75rem 1rem; border-radius: 9999px;
      border: 1px solid #334155; background: #1e293b; color: white; font-size: 0.9rem;
    }
    .fh-newsletter-input input::placeholder { color: #64748b; }
    .fh-newsletter-btn {
      background: #2563eb; color: white; border: none; border-radius: 9999px;
      padding: 0.75rem 1.25rem; font-weight: 600; cursor: pointer; transition: all 0.3s;
    }
    .fh-newsletter-btn:hover { background: #1d4ed8; transform: translateY(-2px); }

    .fh-footer-bottom {
      border-top: 1px solid #1e293b; padding-top: 2rem; margin-top: 2rem;
      display: flex; flex-direction: column; align-items: center; justify-content: space-between; gap: 1rem;
    }
    @media (min-width: 768px) { .fh-footer-bottom { flex-direction: row; } }
    .fh-copyright { color: #64748b; font-size: 0.875rem; }
    .fh-footer-bottom-links { display: flex; gap: 2rem; align-items: center; flex-wrap: wrap; justify-content: center; }
    .fh-footer-bottom-links a { color: #94a3b8; text-decoration: none; font-size: 0.875rem; transition: color 0.2s; }
    .fh-footer-bottom-links a:hover { color: white; }
    .fh-seo9ine-link {
      display: inline-flex; align-items: center; gap: 0.5rem;
      color: #f59e0b !important; font-weight: 700; text-decoration: none; transition: all 0.3s;
    }
    .fh-seo9ine-link:hover { color: #fbbf24 !important; transform: scale(1.05); }

    @keyframes heartbeat { 0%,100%{transform:scale(1)} 25%{transform:scale(1.1)} 50%{transform:scale(1)} 75%{transform:scale(1.1)} }
    .animate-heartbeat { animation: heartbeat 1.5s ease-in-out infinite; }
  `;

  const HeaderContent = () => (
    <header className={`fh-header ${scrolled ? 'scrolled' : 'default'}`}>
      <div className="fh-container">
        <div className="fh-header-content">
          {/* Logo — image only, no text */}
          <div className="fh-logo" onClick={() => navigate('/')}>
            <img src="/images/logo.png" alt="Fresh Hills"
              onError={e => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/46?text=FH'; }} />
          </div>
          <nav className="fh-nav">
            <a href="/" className="fh-nav-link">Home</a>
            <a href="/#features" className="fh-nav-link">Features</a>
            <a href="/#offers" className="fh-nav-link">Offers</a>
            <a href="/#how-it-works" className="fh-nav-link">How It Works</a>
            <a href="/#contact" className="fh-nav-link">Contact</a>
          </nav>
          <div className="flex items-center gap-2 md:gap-3">
            <button onClick={() => navigate('/products')} className="fh-shop-btn">
              <ShoppingCartIcon className="w-5 h-5" />
              <span className="hidden sm:inline">Shop</span>
            </button>
            <button onClick={() => navigate('/scan')} className="fh-btn fh-btn-primary">
              <QrCodeIcon className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden sm:inline">Scan</span>
            </button>
            <div className="fh-cart-icon" onClick={() => navigate('/cart')}>
              <ShoppingCartIcon className="h-6 w-6 text-gray-600" />
              {cartCount > 0 && <span className="fh-cart-badge">{cartCount > 9 ? '9+' : cartCount}</span>}
            </div>
          </div>
        </div>
      </div>
    </header>
  );

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="min-h-screen bg-gray-50 flex flex-col">
          <HeaderContent />
          <div className="flex-1 flex justify-center items-center">
            <div className="fh-spinner"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="min-h-screen bg-gray-50">
        <HeaderContent />

        {/* Main Content */}
        <div className="fh-confirmation-container">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 mb-4 transition-colors">
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to Home</span>
          </button>

          <div className="fh-confirmation-card">
            <div className="fh-success-icon"><CheckCircleIcon /></div>
            <h1 className="fh-confirmation-title">Order Confirmed!</h1>
            <p className="fh-confirmation-subtitle">Thank you for your order. We're getting it ready for delivery.</p>

            <div className="fh-order-details">
              <div className="fh-order-row">
                <span className="fh-order-label"><SparklesIcon /> Order Number</span>
                <span className="fh-order-value fh-order-number">#{orderNumber}</span>
              </div>
              <div className="fh-order-row">
                <span className="fh-order-label"><ClockIcon /> Order Date & Time</span>
                <span className="fh-order-value">
                  {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="fh-order-row">
                <span className="fh-order-label"><TruckIcon /> Estimated Delivery</span>
                <span className="fh-order-value">
                  {new Date(Date.now() + 45 * 60000).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </span>
              </div>
              <div className="fh-order-row">
                <span className="fh-order-label"><CurrencyRupeeIcon /> Total Amount</span>
                <span className="fh-order-value fh-order-total">₹{order?.total_amount || 0}</span>
              </div>
              {order?.payment_method && (
                <div className="fh-order-row">
                  <span className="fh-order-label"><QrCodeIcon /> Payment Method</span>
                  <span className="fh-order-value">{order.payment_method}</span>
                </div>
              )}
            </div>

            <div className="fh-action-buttons">
              <button onClick={() => navigate('/products')} className="fh-action-btn fh-action-btn-primary">
                <ShoppingCartIcon className="w-5 h-5" /> Continue Shopping
              </button>
              <button onClick={() => navigate('/')} className="fh-action-btn fh-action-btn-secondary">
                <HomeIcon className="w-5 h-5" /> Go to Home
              </button>
            </div>

            <div className="fh-tracking-badge">
              <DevicePhoneMobileIcon />
              <div>
                <div className="fh-tracking-title">📱 Order Tracking</div>
                <div className="fh-tracking-desc">You will receive SMS updates at {order?.phone || 'your registered mobile number'}</div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Need help? Contact our support at{' '}
                <a href="tel:+917010844160" className="text-emerald-600 hover:text-emerald-700 font-medium">+91 70108 44160</a>
              </p>
            </div>
          </div>
        </div>

        {/* Trust Bar */}
        <div className="fh-trust-banner">
          <div className="fh-container">
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {[
                { bg: '#d1fae5', icon: <BoltIcon className="w-4 h-4 text-emerald-600" />, label: 'Same Day Delivery' },
                { bg: '#dbeafe', icon: <CurrencyRupeeIcon className="w-4 h-4 text-blue-600" />, label: 'Cashless Payment' },
                { bg: '#fef3c7', icon: <CubeIcon className="w-4 h-4 text-amber-600" />, label: 'Fresh Stock Daily' },
                { bg: '#fae8ff', icon: <UserGroupIcon className="w-4 h-4 text-purple-600" />, label: '10k+ Customers' },
              ].map(({ bg, icon, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <div style={{ background: bg, padding: '0.5rem', borderRadius: '9999px' }}>{icon}</div>
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="fh-footer">
          <div className="fh-container">
            <div className="fh-footer-grid">
              <div>
                {/* Footer logo — image only, no text */}
                <div className="fh-footer-logo" onClick={() => navigate('/')}>
                  <img src="/images/logo.png" alt="Fresh Hills"
                    onError={e => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/44?text=FH'; }} />
                </div>
                <p className="fh-footer-about">India's first smart fridge network bringing fresh dairy products to your doorstep 24/7. No queues, no cashiers, just pure convenience since 2024.</p>
                <div className="fh-footer-social">
                  {[
                    "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
                    "M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84",
                    "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"
                  ].map((path, i) => (
                    <a key={i} href="#" className="fh-social-icon">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d={path} /></svg>
                    </a>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="fh-footer-title"><HeartIcon className="w-5 h-5 fh-footer-heart" /> Quick Links</h4>
                <ul className="fh-footer-links">
                  {[['/', 'Home'], ['/#features', 'Features'], ['/#offers', 'Offers'], ['/#how-it-works', 'How It Works'], ['/#contact', 'Contact']].map(([href, label]) => (
                    <li key={label}><a href={href}><ChevronRightIcon className="w-3 h-3" /> {label}</a></li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="fh-footer-title"><HeartIcon className="w-5 h-5 fh-footer-heart" /> Our Products</h4>
                <ul className="fh-footer-links">
                  {[['milk','Fresh Milk'],['butter','Butter & Cream'],['cheese','Cheese'],['eggs','Farm Eggs'],['yogurt','Curd & Yogurt']].map(([cat, label]) => (
                    <li key={cat}><a href={`/products?category=${cat}`}><ChevronRightIcon className="w-3 h-3" /> {label}</a></li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="fh-footer-title"><HeartIcon className="w-5 h-5 fh-footer-heart" /> Contact Us</h4>
                <ul className="fh-footer-contact">
                  <li><MapPinIcon /><span>36/845, Fesicon Enterprises, Near Sunrise Hospital, Sea Port - Air Port Road, Kakkanad, Thrikkakara, Ernakulam, Kerala, 682021</span></li>
                  <li><DevicePhoneMobileIcon /><span>+91 70108 44160</span></li>
                  <li>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>hello@fesiconenterprises.com</span>
                  </li>
                </ul>
                <div className="fh-footer-newsletter">
                  <h5 style={{ color: 'white', fontSize: '0.95rem', fontWeight: '600', marginBottom: '1rem' }}>Subscribe for offers</h5>
                  <div className="fh-newsletter-input">
                    <input type="email" placeholder="Your email address" />
                    <button className="fh-newsletter-btn">Subscribe</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="fh-footer-bottom">
              <div className="fh-copyright">
                <span>© {currentYear} FESICON ENTERPRISE. All rights reserved. ❤️ Made in India</span>
                <br />
                <span style={{ fontSize: '0.7rem', display: 'block', marginTop: '0.25rem' }}>GST: 32AAGFF6427Q1Z2</span>
              </div>
              <div className="fh-footer-bottom-links">
                <a href="#">Privacy Policy</a>
                <a href="#">Terms of Service</a>
                <a href="#">Cookie Policy</a>
                <a href="https://seo9ine.com" target="_blank" rel="noopener noreferrer" className="fh-seo9ine-link">
                  <span>SEO9INE</span>
                  <HeartIcon className="w-4 h-4 animate-heartbeat" style={{ color: '#ef4444' }} />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default OrderConfirmation;